import React from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { hasActiveSubscription } from "../../utils/subscription";
import { useAppLanguage } from "../../utils/appLanguage";
import { LIMIT_LOCK, PLAN_PRICES, PLANS, type LandingLangCode } from "../landing/landingContent";
import { createLandingCopy, isLandingLang } from "../landing/landingCopy";
import { formatPlanPrice, yearlySavingPercent } from "../landing/PlansSection";
import { useAstriaCopy, type CopyKey } from "./copy";

/** The app uses bare codes where the landing copy uses regional ones. */
const LANDING_CODE: Record<string, LandingLangCode> = { zh: "zh-CN", pt: "pt-BR" };

const TESTER_ROLE_ID = 3;

interface LockDetails {
  /** Sidebar label of the tab, shown as the small heading. */
  tabLabel?: CopyKey;
  title: string;
  text: string;
  benefits: readonly string[];
}

const PRO_FEATURES = PLANS.tiers.find((tier) => tier.id === "pro")?.features ?? [];

/** Tabs a Free user cannot open, with what the lock screen says about each. */
export const LOCKED_TABS: Readonly<Record<string, LockDetails>> = {
  "/astrology": {
    tabLabel: "navAstrology",
    title: "Astrology opens with Astria Pro",
    text: "Upgrade to read your full sky in plain language, with your complete Life Blueprint and relationship insights.",
    benefits: [PRO_FEATURES[2], PRO_FEATURES[3], PRO_FEATURES[0]].filter(Boolean),
  },
};

/** Shown when a free user reaches today's limit (preview it at /lock). */
const LIMIT_DETAILS: LockDetails = {
  title: LIMIT_LOCK.title,
  text: LIMIT_LOCK.text,
  benefits: PRO_FEATURES.slice(0, 4),
};

/** The upgrade card: sits in the content area, so the sidebar stays visible beside it. */
export const LockCard: React.FC<{ details: LockDetails }> = ({ details }) => {
  const navigate = useNavigate();
  const appLang = useAppLanguage();
  const c = useAstriaCopy();
  const lang: LandingLangCode = LANDING_CODE[appLang] ?? (isLandingLang(appLang) ? appLang : "en");
  const { tr } = createLandingCopy(lang);

  const { monthly, yearly } = PLAN_PRICES.USD.pro;
  const saving = yearlySavingPercent(monthly, yearly);

  return (
    <div className="flex min-h-[80vh] items-center justify-center px-5 py-10">
      <main
        className="w-full max-w-md rounded-[28px] p-7 text-center sm:p-9"
        style={{
          background: "var(--astria-surface)",
          border: "1px solid rgba(201,191,255,0.35)",
          boxShadow: "var(--astria-shadow-card), 0 0 80px -30px var(--astria-accent-primary)",
          color: "var(--astria-text-primary)",
        }}
      >
        <div
          className="mx-auto mb-5 grid h-14 w-14 place-items-center rounded-full"
          style={{
            background: "rgba(167,139,255,0.14)",
            border: "0.5px solid rgba(201,191,255,0.5)",
            color: "var(--astria-accent-primary)",
          }}
        >
          <Lock size={24} strokeWidth={1.6} aria-hidden="true" />
        </div>

        <p className="text-xs uppercase tracking-[0.18em]" style={{ color: "var(--astria-accent-primary)" }}>
          {details.tabLabel ? `${c(details.tabLabel)} · ${tr("Included in Astria Pro")}` : tr("Astria Pro")}
        </p>
        <h1 className="mt-3 text-3xl font-semibold leading-tight" style={{ fontFamily: "Fraunces, Georgia, serif" }}>
          {tr(details.title)}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed" style={{ color: "var(--astria-text-secondary)" }}>
          {tr(details.text)}
        </p>

        <ul className="mt-6 space-y-2 text-left text-[15px]">
          {details.benefits.map((benefit) => (
            <li key={benefit} className="flex gap-3">
              <span aria-hidden="true" style={{ color: "var(--astria-accent-primary)" }}>
                ✓
              </span>
              {tr(benefit)}
            </li>
          ))}
        </ul>

        <div className="mt-7 rounded-2xl px-4 py-4" style={{ background: "var(--astria-surface-soft)" }}>
          <p className="text-sm" style={{ color: "var(--astria-text-secondary)" }}>
            {tr("Astria Pro")}
          </p>
          <p className="mt-1" dir="ltr">
            <b className="text-2xl">{formatPlanPrice(monthly, "USD")}</b>{" "}
            <span className="text-sm" style={{ color: "var(--astria-text-secondary)" }}>
              {tr("per month")}
            </span>
          </p>
          <p className="mt-1 text-sm" style={{ color: "var(--astria-text-secondary)" }}>
            <span dir="ltr">{formatPlanPrice(yearly, "USD")}</span> {tr("per year")} · {tr("Save")} {saving}%
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate("/account/plans-billing")}
          className="mt-6 w-full rounded-full px-6 py-3.5 text-[15px] font-medium text-white"
          style={{ background: "linear-gradient(135deg, #d9cfff, #a78bff)", boxShadow: "var(--astria-shadow-button)" }}
        >
          {tr(LIMIT_LOCK.cta.label)} ✦
        </button>
        <button
          type="button"
          onClick={() => navigate("/today")}
          className="mt-3 w-full rounded-full px-6 py-3 text-[15px]"
          style={{ color: "var(--astria-text-secondary)" }}
        >
          {tr("Maybe Later")}
        </button>
      </main>
    </div>
  );
};

/** /lock: previews the daily-limit version of the card inside the app shell. */
export const LockPreview: React.FC = () => <LockCard details={LIMIT_DETAILS} />;

/**
 * Wraps a tab's page. Free users see the lock card in its place; the sidebar is
 * untouched, so they can still move around. Subscribers and testers pass through.
 */
export const TabGate: React.FC<{ path: string; children: React.ReactNode }> = ({ path, children }) => {
  const { user, isAuthenticated } = useAuth();
  const details = LOCKED_TABS[path];

  // Until the profile has loaded we cannot tell, so show the page rather than flash a lock.
  if (!details || !isAuthenticated || !user) return <>{children}</>;
  if (user.roleId === TESTER_ROLE_ID || hasActiveSubscription(user)) return <>{children}</>;
  return <LockCard details={details} />;
};
